import { useState } from 'react';
import { useIntl } from 'react-intl';

import { UserFlagsDisplayFlag } from '@xing-com/crate-common-graphql-types';
import type { FlagSize, Flag } from '@xing-com/flag';
import { useViewerData } from '@xing-com/hub';
import { usePopOver } from '@xing-com/pop-over';
import { BREAKPOINTS, useMediaListener } from '@xing-com/util';

import { UserFlagContent } from './common';
import { FlagBottomSheet } from './flag-bottom-sheet';
import { FlagPopOver } from './flag-pop-over';
import { Styled } from './styles';

const flagVariantMap: Record<
  UserFlagsDisplayFlag,
  React.ComponentProps<typeof Flag>['variant']
> = {
  [UserFlagsDisplayFlag.Ambassador]: 'ambassador',
  [UserFlagsDisplayFlag.Basic]: 'basic',
  [UserFlagsDisplayFlag.Insider]: 'insider',
  [UserFlagsDisplayFlag.Moderator]: 'moderator',
  [UserFlagsDisplayFlag.Premium]: 'premium',
  [UserFlagsDisplayFlag.ProCoach]: 'proCoach',
  [UserFlagsDisplayFlag.ProTrainer]: 'proTrainer',
  [UserFlagsDisplayFlag.Executive]: 'premium', // can be removed
};

export type UserFlagInfoType = {
  title?: string;
  body?: string[];
  action?: {
    text: string;
    url: string;
    urn: null;
    trackingId: null;
  };
};

export type UserFlagProps = {
  displayFlag: UserFlagsDisplayFlag;
  /**
   * Show the Basic flag (default is hidden)
   */
  showBasicFlag?: boolean;
  size?: FlagSize;
  /**
   * ID of the user that this flag is displaying the membership,
   * although not required, the user flag won't be clickable if value is null
   */
  userId?: number | string;
};

export const UserFlag: React.FC<UserFlagProps> = ({
  displayFlag,
  showBasicFlag = false,
  size = 'medium',
  userId,
  ...props
}) => {
  const intl = useIntl();

  const user = useViewerData().data?.user;
  const viewerDisplayFlag =
    user?.userFlags?.displayFlag || UserFlagsDisplayFlag.Basic;

  const popOver = usePopOver();
  const breakpoint = useMediaListener();
  const isHandheld = breakpoint === BREAKPOINTS.handheld;
  const [showBottomSheet, setShowBottomSheet] = useState(false);

  const displayPopOver = !isHandheld;

  if (displayFlag === UserFlagsDisplayFlag.Basic && !showBasicFlag) return null;

  const variant = flagVariantMap[displayFlag];
  const flag = (
    <Styled.FlagWithPointer size={size} variant={variant} {...props} />
  );
  // all the data should be provided for the click feature to work
  const hasClickableFeature = userId && displayFlag;
  if (!hasClickableFeature) return flag;

  const isViewerPremium = viewerDisplayFlag === UserFlagsDisplayFlag.Premium;
  const premiumTheme = isViewerPremium
    ? displayFlag === UserFlagsDisplayFlag.Premium
    : displayFlag === UserFlagsDisplayFlag.Premium ||
      displayFlag === UserFlagsDisplayFlag.Basic;

  const commonProps = { premiumTheme, displayFlag, userId: userId.toString() };

  return (
    <Styled.FlagWrapper>
      <Styled.UserFlagButton
        aria-label={intl.formatMessage(
          { id: 'USER_FLAG_BUTTON_ARIA_LABEL' },
          { type: displayFlag }
        )}
        onClick={async (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          if (displayPopOver) {
            popOver.handleShow();
          } else {
            setShowBottomSheet(true);
          }
        }}
        ref={displayPopOver ? popOver.triggerRef : undefined}
      >
        {flag}
      </Styled.UserFlagButton>
      {displayPopOver ? (
        <FlagPopOver
          onCloseClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            popOver.handleHide();
          }}
          onOutsideClick={popOver.handleHide}
          triggerRef={popOver.triggerRef}
          show={popOver.show}
          premiumTheme={premiumTheme}
          isInFlow={true}
        >
          <UserFlagContent show={popOver.show} {...commonProps} />
        </FlagPopOver>
      ) : (
        <FlagBottomSheet
          dimmerTitle="user-flag-close"
          showBottomSheet={showBottomSheet}
          setShowBottomSheet={setShowBottomSheet}
          premiumTheme={premiumTheme}
        >
          <UserFlagContent show={showBottomSheet} {...commonProps} />
        </FlagBottomSheet>
      )}
    </Styled.FlagWrapper>
  );
};
